import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import CloseIcon from "@material-ui/icons/Close";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { Grid, Typography, Button, TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import styled from "styled-components/macro";

import Dialog from "../shared/Dialog";
import useModal from "../../hooks/useModal";
import { useModals } from "../../hooks/useModals";
import { useAppContext } from "../../api/AppContext";
import { useSnackbar } from "notistack";
import ImageInsertModal from "../shared/ImageInsertModal";
import Img from "../shared/Image";

const TileImageFrame = styled.div`
  cursor: pointer;
  width: auto;
  max-width: 100%;
  height: auto;
  max-height: 100%;
  display: block;
  border-radius: 8px;
  border: 3px solid ${(props) => (props.$selected ? "#1976d2" : "#ddd")};
  box-sizing: border-box;
  box-shadow: ${(props) =>
    props.$selected
      ? "0 0 6px rgba(25,118,210,0.6)"
      : "0 1px 2px rgba(0,0,0,0.08)"};
  transition: transform 0.15s ease, box-shadow 0.15s ease,
    border-color 0.15s ease;
  &:hover {
    border-color: #1976d2;
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.12);
  }
  > img {
    width: auto;
    max-width: 100%;
    height: auto;
    max-height: 100%;
    display: block;
    border-radius: 5px;
  }
`;

const DialogContentContainer = styled(DialogContent)`
  background: #fafafa;
  display: flex;
  flex-direction: row;
  gap: 24px;
  /* Let inner columns manage their own overflow */
  flex: 1 1 auto;
  min-height: 0;
  max-height: calc(100vh - 160px);
  overflow: hidden;
  padding: 24px;
`;

const DialogTitleContainer = styled(DialogTitle)`
  background: #fafafa;
  padding: 16px 24px;
  border-bottom: 1px solid #e0e0e0;
`;

const LeftColumn = styled(Box)`
  width: 260px;
  flex: 0 0 260px;
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.1);
`;

const RightColumn = styled(Box)`
  flex-grow: 1;
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  min-height: 0; /* allow inner scroll area to calculate height */
  overflow: hidden; /* clip inner scroll only to this column */
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.1);
`;

const ImagesGrid = styled(Grid)`
  flex-wrap: wrap;
  gap: 16px;
  width: 100%;
  min-width: 0; /* avoid overflow when scrollbars take space */
  box-sizing: border-box;
`;

const ImagesScrollArea = styled(Box)`
  flex: 1 1 auto;
  min-height: 0;
  /* Force persistent vertical scrollbar to avoid layout shifts */
  overflow-y: scroll;
  /* Reserve space consistently and keep content from sitting under the scrollbar */
  scrollbar-gutter: stable both-edges;
  padding-right: 8px; /* create gutter so tiles never get clipped under the scrollbar */
  margin-right: -8px; /* neutralize visual shrink by pulling the scroll area to the edge */

  /* Cross-browser thin scrollbar styling */
  scrollbar-width: thin; /* Firefox */
  scrollbar-color: rgba(0, 0, 0, 0.35) transparent; /* Firefox */

  /* Chromium/WebKit */
  &::-webkit-scrollbar {
    width: 10px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.35);
    border-radius: 8px;
    border: 2px solid transparent; /* gives the thumb some inset spacing */
    background-clip: content-box;
  }
`;

const ImageWrapper = styled(Box)`
  width: 100%;
  height: 200px; /* fixed visual slot to align subsequent controls/buttons */
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden; /* guard against any accidental overflow */
  background: transparent;
  position: relative; /* allow badge to overlay within the image area */
`;

const ImageLoadingState = styled(Box)`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
  color: #666;
  padding: 12px;
`;

const ImageTile = styled(Box)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
`;

const SelectedBadge = styled(Box)`
  position: absolute;
  top: 8px;
  right: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(25, 118, 210, 0.9);
  color: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
`;

const TileActions = styled(Box)`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
`;

const ControlRow = styled(Box)`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  min-height: 56px; /* keep name + button area consistent across tiles */
`;

const BottomBar = styled(Box)`
  display: flex;
  justify-content: flex-end;
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid #eee;
`;

const FilteredGalleryImagePreviewModal = ({
  galleryIds,
  galleries: galleriesProp,
  multipleImages = false,
  initialSelectedGalleryIds = [],
  field,
  fieldName,
  onlyPublished = false,
  onConfirm,
  onClose,
}) => {
  const {
    listImageGalleries,
    listGalleryImages,
    updateImageGallery,
    listImageFields,
    user,
    updateTeamsGalleryImage,
  } = useAppContext();

  const { enqueueSnackbar } = useSnackbar();

  const DIGITALATTITUDE_COMPANY_ID = "5706867a-c563-4ca5-b803-c1883c38b640";
  const isDAUser = !!(
    user &&
    Array.isArray(user.companies) &&
    user.companies.includes(DIGITALATTITUDE_COMPANY_ID)
  );

  const [loadingGalleries, setLoadingGalleries] = useState(false);
  const [loadingImages, setLoadingImages] = useState(false);
  const [galleries, setGalleries] = useState([]);
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [images, setImages] = useState([]);
  const [selectedImageIds, setSelectedImageIds] = useState(new Set());
  const [resolvedFieldId, setResolvedFieldId] = useState(field || null);
  const [isInsertOpen, setIsInsertOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [nameEdits, setNameEdits] = useState({});
  const [savingName, setSavingName] = useState({});
  const [imageSearch, setImageSearch] = useState("");
  const [previewRetryingByKey, setPreviewRetryingByKey] = useState({});

  const deriveNameFromUrl = useCallback((u) => {
    try {
      if (!u) return "";
      const qIndex = u.indexOf("?");
      const clean = qIndex >= 0 ? u.substring(0, qIndex) : u;
      const lastSlash = clean.lastIndexOf("/");
      return lastSlash >= 0 ? clean.substring(lastSlash + 1) : clean;
    } catch (_) {
      return "";
    }
  }, []);

  const handleSaveName = useCallback(
    async (image) => {
      const rawId = image?.id || image?.image_id || image?.imageId;
      const id = rawId && String(rawId);
      if (!id) {
        enqueueSnackbar("Cannot rename: missing image id", {
          variant: "warning",
        });
        return;
      }
      const key = String(id || image.url);
      const current = image?.imagename || "";
      const next = (nameEdits[key] ?? current).trim();
      if (next === current) return;
      if (!next) {
        enqueueSnackbar("Name cannot be empty", { variant: "warning" });
        return;
      }
      try {
        setSavingName((prev) => ({ ...prev, [key]: true }));
        await updateTeamsGalleryImage({
          isNew: false,
          imageId: id,
          imagename: next,
        });
        setImages((prev) =>
          (Array.isArray(prev) ? prev : []).map((img) => {
            const imgId = String(
              img?.id || img?.image_id || img?.imageId || ""
            );
            return imgId === id ? { ...img, imagename: next } : img;
          })
        );
        setNameEdits((prev) => {
          const { [key]: _omit, ...rest } = prev || {};
          return rest;
        });
        enqueueSnackbar("Image name updated", {
          variant: "success",
          autoHideDuration: 2000,
        });
      } catch (e) {
        console.error("Rename image failed", e);
        enqueueSnackbar(e?.message || "Failed to update image name", {
          variant: "error",
          autoHideDuration: 3000,
        });
      } finally {
        setSavingName((prev) => ({ ...prev, [key]: false }));
      }
    },
    [deriveNameFromUrl, nameEdits, enqueueSnackbar, updateTeamsGalleryImage]
  );

  const canWrite = useMemo(() => {
    if (isDAUser) return true;
    const companies = Array.isArray(selectedGallery?.companies)
      ? selectedGallery.companies
      : [];
    const access = companies.map((c) => c && c.accessType).filter(Boolean);
    return access.includes("write");
  }, [selectedGallery, isDAUser]);

  const { handleConfirm } = useModal({
    onClose,
    onConfirm: useCallback(
      (payload) => onConfirm && onConfirm(payload),
      [onConfirm]
    ),
  });

  const modals = useModals();

  const [isNewField, setIsNewField] = useState(false);
  const [fieldsChecked, setFieldsChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const checkField = async () => {
      if (!field && !fieldName) {
        if (!cancelled) setFieldsChecked(true);
        return;
      }
      try {
        const list = await listImageFields?.();
        const arr = Array.isArray(list) ? list : [];

        let found = null;
        if (field) {
          found = arr.find((f) => String(f?.id) === String(field));
          if (found?.id && !cancelled) setResolvedFieldId(found.id);
        }
        if (!found && fieldName) {
          const nameLc = String(fieldName).toLowerCase();
          found = arr.find(
            (f) => String(f?.name || "").toLowerCase() === nameLc
          );
          if (found?.id && !cancelled) setResolvedFieldId(found.id);
        }
        if (!cancelled) {
          setIsNewField(!found);
          setFieldsChecked(true);
        }
      } catch (e) {
        console.warn("Failed to list image fields to check field existence", e);
        if (!cancelled) {
          setIsNewField(true);
          setFieldsChecked(true);
        }
      }
    };
    checkField();
    return () => {
      cancelled = true;
    };
  }, [field, fieldName, listImageFields]);

  const lastRequestKeyRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (Array.isArray(galleriesProp) && galleriesProp.length) {
        const list = onlyPublished
          ? (galleriesProp || []).filter(
              (g) => g?.is_published === true || g?.isPublished === true
            )
          : galleriesProp;
        setGalleries(list);
        lastRequestKeyRef.current = "prop-galleries";
        return;
      }

      if (!fieldsChecked) return;

      const requestKey = JSON.stringify({
        galleryIds: (galleryIds || []).map(String).sort(),
        fieldId: resolvedFieldId || null,
        isNewField,
        onlyPublished,
      });

      if (lastRequestKeyRef.current === requestKey) return;
      lastRequestKeyRef.current = requestKey;

      setLoadingGalleries(true);
      try {
        const res = await listImageGalleries?.({
          ...(onlyPublished ? { published: true } : {}),
        });
        const list = Array.isArray(res) ? res : [];
        if (mounted) setGalleries(list);
      } finally {
        if (mounted) setLoadingGalleries(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [
    galleriesProp,
    galleryIds,
    listImageGalleries,
    resolvedFieldId,
    fieldsChecked,
    isNewField,
  ]);

  useEffect(() => {
    if (!selectedGallery && galleries.length > 0) {
      const pre = initialSelectedGalleryIds?.length
        ? galleries.find(
            (g) => String(g.id) === String(initialSelectedGalleryIds[0])
          )
        : galleries[0];
      if (pre) setSelectedGallery(pre);
    }
  }, [galleries, initialSelectedGalleryIds, selectedGallery]);

  const galleryOptions = useMemo(() => {
    const list = Array.isArray(galleries) ? galleries : [];
    if (isNewField) {
      return list;
    }
    if (!resolvedFieldId) return list;
    return list.filter((g) => {
      const ids = g?.field_ids || g?.fieldIds || [];
      const hasBlacklist = Array.isArray(ids)
        ? ids.map(String).includes(String(resolvedFieldId))
        : false;
      return !hasBlacklist;
    });
  }, [galleries, isNewField, resolvedFieldId]);

  useEffect(() => {
    const opts = Array.isArray(galleryOptions) ? galleryOptions : [];
    if (
      selectedGallery &&
      !opts.some((g) => String(g.id) === String(selectedGallery.id))
    ) {
      setSelectedGallery(opts[0] || null);
    }
  }, [galleryOptions, selectedGallery]);

  const reloadImages = useCallback(async () => {
    if (!selectedGallery) return;

    setLoadingImages(true);
    setImages([]);
    setPreviewRetryingByKey({});

    try {
      const results = await listGalleryImages?.({
        galleryId: selectedGallery.id,
      });
      const combined = [];
      const seen = new Set();

      (Array.isArray(results) ? results : []).forEach((img) => {
        const key = String(img.id || img.image_id || img.imageId || img.url);
        if (seen.has(key)) return;
        seen.add(key);
        combined.push(img);
      });

      setImages(combined);
    } finally {
      setLoadingImages(false);
    }
  }, [selectedGallery, resolvedFieldId, listGalleryImages]);

  useEffect(() => {
    let isMounted = true;
    const loadImages = async () => {
      try {
        await reloadImages();
      } catch (err) {
        if (isMounted) {
          console.error("Failed to load images:", err);
        }
      }
    };
    loadImages();
    return () => {
      isMounted = false;
    };
  }, [reloadImages]);

  const toggleImage = useCallback(
    (image) => {
      if (!multipleImages) {
        handleConfirm(image);
        return;
      }
      setSelectedImageIds((prev) => {
        const next = new Set(prev);
        const key = String(
          image.id || image.image_id || image.imageId || image.url
        );
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    },
    [multipleImages, handleConfirm]
  );

  const handleDeleteOne = useCallback(
    async (image, e) => {
      e?.stopPropagation?.();
      if (!selectedGallery) return;

      const rawId = image?.id || image?.image_id || image?.imageId;
      const id = rawId && String(rawId);
      if (!id) {
        enqueueSnackbar("Cannot delete: missing image id", {
          variant: "warning",
        });
        return;
      }

      const label = image?.imagename || deriveNameFromUrl(image?.url) || id;

      const doDelete = async () => {
        try {
          setIsDeleting(true);
          await updateImageGallery({
            galleryId: selectedGallery.id,
            removeImageIds: [id],
          });
          enqueueSnackbar("Image removed from gallery", {
            variant: "success",
            autoHideDuration: 2500,
          });
          await reloadImages();
        } catch (e) {
          console.error("Failed to remove image from gallery", e);
          enqueueSnackbar(e?.message || "Failed to remove image", {
            variant: "error",
            autoHideDuration: 3000,
          });
        } finally {
          setIsDeleting(false);
        }
      };

      modals.openConfirmation({
        title: "Remove image from gallery?",
        text: `Are you sure you want to remove "${label}" from the gallery?`,
        confirmCTATitle: "Remove",
        closeCTATitle: "Cancel",
        onConfirm: doDelete,
      });
    },
    [
      deriveNameFromUrl,
      enqueueSnackbar,
      modals,
      reloadImages,
      selectedGallery,
      updateImageGallery,
    ]
  );

  const filteredImages = useMemo(() => {
    const list = Array.isArray(images) ? images : [];
    const term = (imageSearch || "").trim().toLowerCase();
    if (!term) return list;
    return list.filter((img) => {
      const name = (img.imagename || "").toLowerCase();
      const file = (deriveNameFromUrl(img.url) || "").toLowerCase();
      return name.includes(term) || file.includes(term);
    });
  }, [images, imageSearch, deriveNameFromUrl]);

  const imagesGrid = useMemo(() => {
    if (!images || images.length === 0) {
      return (
        <Box
          alignItems="center"
          display="flex"
          height="200px"
          justifyContent="center"
        >
          <Typography color="textSecondary" variant="h6">
            No images available in this gallery
          </Typography>
        </Box>
      );
    }

    if (filteredImages.length === 0) {
      return (
        <Box
          alignItems="center"
          display="flex"
          height="200px"
          justifyContent="center"
        >
          <Typography color="textSecondary" variant="h6">
            No images match your search
          </Typography>
        </Box>
      );
    }

    return (
      <ImagesGrid container>
        {filteredImages.map((image) => {
          const key = String(
            image.id || image.image_id || image.imageId || image.url
          );
          const selected = multipleImages && selectedImageIds.has(key);

          return (
            <Grid key={key} item lg={3} md={4} sm={6} xs={12}>
              <ImageTile>
                <ImageWrapper onClick={() => toggleImage(image)}>
                  <Img
                    retryOnError
                    container={(children) => (
                      <TileImageFrame $selected={selected}>
                        {children}
                      </TileImageFrame>
                    )}
                    loader={
                      <ImageLoadingState>
                        <CircularProgress size={24} />
                        {previewRetryingByKey[key] && (
                          <Typography color="textSecondary" variant="caption">
                            File is being scanned. Retrying shortly...
                          </Typography>
                        )}
                      </ImageLoadingState>
                    }
                    retryDelayMs={10000}
                    retryMaxAttempts={3}
                    src={[image.url]}
                    unloader={
                      <ImageLoadingState>
                        <Typography variant="caption">
                          Image unavailable
                        </Typography>
                        <Typography color="textSecondary" variant="caption">
                          Try again in a few moments.
                        </Typography>
                      </ImageLoadingState>
                    }
                    onRetryStateChange={({ isRetrying }) =>
                      setPreviewRetryingByKey((prev) => ({
                        ...prev,
                        [key]: Boolean(isRetrying),
                      }))
                    }
                  />
                  {multipleImages && selected && (
                    <SelectedBadge aria-label="Selected image">
                      <CheckCircleIcon fontSize="small" />
                    </SelectedBadge>
                  )}
                </ImageWrapper>
                <ControlRow onClick={(e) => e.stopPropagation()}>
                  {canWrite ? (
                    <>
                      <TextField
                        fullWidth
                        label="Name"
                        size="small"
                        value={nameEdits[key] ?? (image.imagename || "")}
                        variant="outlined"
                        onChange={(e) =>
                          setNameEdits((prev) => ({
                            ...prev,
                            [key]: e.target.value,
                          }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            if (
                              !savingName[key] &&
                              (nameEdits[key] ?? (image.imagename || "")) !==
                                (image.imagename || "")
                            ) {
                              handleSaveName(image);
                            }
                          } else if (e.key === "Escape") {
                            e.preventDefault();
                            setNameEdits((prev) => {
                              const { [key]: _omit, ...rest } = prev || {};
                              return rest;
                            });
                          }
                        }}
                      />
                      <Button
                        color="primary"
                        disabled={
                          !!savingName[key] ||
                          (nameEdits[key] ?? (image.imagename || "")) ===
                            (image.imagename || "")
                        }
                        endIcon={
                          savingName[key] ? (
                            <CircularProgress size={25} />
                          ) : null
                        }
                        variant="contained"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSaveName(image);
                        }}
                      >
                        Save
                      </Button>
                    </>
                  ) : (
                    <Typography
                      noWrap
                      color="textSecondary"
                      style={{ width: "100%" }}
                      title={image.imagename || ""}
                      variant="body2"
                    >
                      {image.imagename || ""}
                    </Typography>
                  )}
                </ControlRow>
                {canWrite && (
                  <TileActions>
                    <Button
                      aria-label="Remove this image from gallery"
                      color="secondary"
                      disabled={isDeleting || loadingImages}
                      size="small"
                      startIcon={<DeleteIcon />}
                      variant="outlined"
                      onClick={(e) => handleDeleteOne(image, e)}
                    >
                      Remove
                    </Button>
                  </TileActions>
                )}
              </ImageTile>
            </Grid>
          );
        })}
      </ImagesGrid>
    );
  }, [
    filteredImages,
    multipleImages,
    selectedImageIds,
    toggleImage,
    canWrite,
    handleDeleteOne,
    isDeleting,
    loadingImages,
    nameEdits,
    savingName,
  ]);

  const canConfirm = multipleImages && selectedImageIds.size > 0;
  const selectedImagesArray = useMemo(() => {
    if (!multipleImages) return [];
    const list = Array.isArray(images) ? images : [];
    return list.filter((img) =>
      selectedImageIds.has(
        String(img.id || img.image_id || img.imageId || img.url)
      )
    );
  }, [multipleImages, selectedImageIds, images]);

  return (
    <Dialog fullWidth open maxWidth="lg" scroll="paper">
      <DialogTitleContainer>
        <Box alignItems="center" display="flex" justifyContent="space-between">
          <Typography color="textPrimary" variant="h6">
            Select Image
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitleContainer>

      <DialogContentContainer>
        <LeftColumn>
          <Autocomplete
            fullWidth
            getOptionLabel={(opt) => opt?.name || ""}
            options={galleryOptions}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Gallery"
                placeholder="Select gallery"
                variant="outlined"
              />
            )}
            value={selectedGallery}
            onChange={(_, value) => setSelectedGallery(value)}
          />

          <TextField
            fullWidth
            label="Search images"
            placeholder="Type a name"
            size="small"
            value={imageSearch}
            variant="outlined"
            onChange={(e) => setImageSearch(e.target.value)}
          />

          {loadingGalleries && (
            <Box
              alignItems="center"
              display="flex"
              justifyContent="center"
              mt={2}
            >
              <CircularProgress size={24} />
            </Box>
          )}
        </LeftColumn>

        <RightColumn>
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Tooltip
              title={
                !selectedGallery
                  ? "Select a gallery"
                  : canWrite
                  ? "Add image"
                  : "Read-only gallery"
              }
            >
              <span>
                <IconButton
                  aria-label="Add image"
                  disabled={!selectedGallery || loadingImages || !canWrite}
                  size="small"
                  onClick={() => setIsInsertOpen(true)}
                >
                  <AddIcon style={{ color: "#0000008a" }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {loadingImages && images.length === 0 ? (
            <Box
              alignItems="center"
              display="flex"
              height="300px"
              justifyContent="center"
            >
              <CircularProgress size={40} />
            </Box>
          ) : (
            <ImagesScrollArea>{imagesGrid}</ImagesScrollArea>
          )}

          {multipleImages && (
            <BottomBar>
              <Button
                color="primary"
                disabled={!canConfirm}
                variant="contained"
                onClick={() => handleConfirm(selectedImagesArray)}
              >
                Confirm selection{" "}
                {canConfirm ? `(${selectedImagesArray.length})` : ""}
              </Button>
            </BottomBar>
          )}
        </RightColumn>
      </DialogContentContainer>

      <ImageInsertModal
        gallery={selectedGallery}
        open={isInsertOpen}
        onClose={() => setIsInsertOpen(false)}
        onSuccess={() => {
          setIsInsertOpen(false);
          reloadImages();
        }}
      />
    </Dialog>
  );
};

FilteredGalleryImagePreviewModal.propTypes = {
  galleryIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  galleries: PropTypes.array,
  multipleImages: PropTypes.bool,
  initialSelectedGalleryIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  field: PropTypes.string,
  fieldName: PropTypes.string,
  onlyPublished: PropTypes.bool,
  onConfirm: PropTypes.func,
  onClose: PropTypes.func,
};

export default FilteredGalleryImagePreviewModal;
